import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BatchCreateResponse,
  BatchStatusResponse,
  BinanceWallet,
  BinanceWalletListFilters,
  BinanceWalletListResponse,
  DashboardResponse,
  DepositAddressResponse,
  DepositCoin,
  DepositNetwork,
  DepositRecord,
  MasterSnapshotHistoryResponse,
  OnboardResponseDto,
  PrevalidacionStatusResponse,
  RevalidateKycResponse,
  StartPrevalidacionResponse,
  UsersWithWalletFilters,
  UsersWithWalletListResponse,
  WalletTrc20ResponseDto,
} from '../interfaces/binance-wallet.interface';

@Injectable({
  providedIn: 'root',
})
export class BinanceWalletAdminService {
  private baseUrl = `${environment.apiNest}binance-link/wallets`;

  constructor(private http: HttpClient) {}

  /**
   * Listado admin de usuarios con (o sin) wallet Binance Link.
   * Vista admin principal: combina datos del `User` con wallet activa via
   * LEFT JOIN. Filtros: `q` (busqueda), `hasWallet` (all/with/without).
   */
  listUsersWithWallet(
    filters: UsersWithWalletFilters = {},
  ): Promise<UsersWithWalletListResponse> {
    let params = new HttpParams();
    if (filters.page != null) params = params.set('page', String(filters.page));
    if (filters.limit != null) params = params.set('limit', String(filters.limit));
    if (filters.q) params = params.set('q', filters.q);
    if (filters.hasWallet) params = params.set('hasWallet', filters.hasWallet);
    if (filters.usuarioId != null)
      params = params.set('usuarioId', String(filters.usuarioId));
    return firstValueFrom(
      this.http.get<UsersWithWalletListResponse>(
        `${this.baseUrl}/users-with-wallet`,
        { params },
      ),
    );
  }

  listWallets(
    filters: BinanceWalletListFilters = {},
  ): Promise<BinanceWalletListResponse> {
    let params = new HttpParams();
    if (filters.page != null) params = params.set('page', String(filters.page));
    if (filters.limit != null)
      params = params.set('limit', String(filters.limit));
    if (filters.usuarioId != null)
      params = params.set('usuarioId', String(filters.usuarioId));
    if (filters.subAccountId)
      params = params.set('subAccountId', filters.subAccountId);
    if (filters.walletStatus)
      params = params.set('walletStatus', filters.walletStatus);
    if (filters.kycStatus) params = params.set('kycStatus', filters.kycStatus);
    if (filters.onlyActive != null)
      params = params.set('onlyActive', filters.onlyActive ? 'true' : 'false');

    return firstValueFrom(
      this.http.get<BinanceWalletListResponse>(this.baseUrl, { params }),
    );
  }

  getWalletById(id: string): Promise<BinanceWallet> {
    return firstValueFrom(this.http.get<BinanceWallet>(`${this.baseUrl}/${id}`));
  }

  /**
   * POST /binance-link/wallets/onboard
   *
   * Reemplaza al flujo legacy (`POST /for-user` + `POST /:id/kyc/submit`).
   * Crea o reutiliza la subcuenta Binance y decide si toca enviar KYC.
   * Idempotente del lado backend: si el usuario ya tiene wallet ACTIVE y
   * `kycStatus = PASS`, devuelve `subAccountAction: REUSED,
   * kycAction: SKIPPED_APPROVED` sin tocar Binance.
   *
   * Si `response.expectsWebhook = true`, el caller debe iniciar polling
   * de `listUsersWithWallet({ usuarioId })` con el backoff del brief §6.
   *
   * Errores tipados que el caller debe manejar:
   *  - 400 `estadoComplianceId` → compliance interno no aprobado.
   *  - 400 `missing: string[]` → faltan campos KYC en el `User`.
   *  - 404 → usuario no existe.
   *  - 409 `kycSaasCode`/`kycSaasMessage` → Binance rechazó el submit KYC.
   *  - 502 → error temporal de red contra Binance.
   */
  onboardUser(usuarioId: number): Promise<OnboardResponseDto> {
    return firstValueFrom(
      this.http.post<OnboardResponseDto>(`${this.baseUrl}/onboard`, {
        usuarioId,
      }),
    );
  }

  /**
   * POST /binance-link/wallets/:walletId/wallet-trc20
   *
   * Obtiene (o crea por primera vez) la deposit address USDT TRC20.
   * Reemplaza al legacy `GET /:id/deposit-address` (`getDepositAddressForWallet`).
   *
   * Idempotente: si la address ya está persistida en
   * `binance_link_deposit_addresses`, responde `source: CACHE` sin tocar
   * Binance. La próxima carga del modal vendrá ya con `walletTrc20`
   * poblado desde `GET /users-with-wallet`, lo que evita reinvocar este
   * POST. Solo se invoca explícitamente con click en "Crear Wallet".
   *
   * Errores tipados:
   *  - 404 → wallet no existe (reintentar paso 1).
   *  - 409 `kycStatus` → "El usuario se encuentra pendiente de aprobación
   *    de KYC". Race condition con el webhook; refrescar status.
   *  - 409 `walletStatus` → la wallet no está operativa (BLOCKED/ERROR).
   *  - 502 → error temporal de red contra Binance.
   */
  createTrc20Wallet(walletId: string): Promise<WalletTrc20ResponseDto> {
    return firstValueFrom(
      this.http.post<WalletTrc20ResponseDto>(
        `${this.baseUrl}/${walletId}/wallet-trc20`,
        {},
      ),
    );
  }

  /**
   * Crea wallet para un usuario especifico (path operador).
   *
   * Nota: el path NO contiene `/admin/`. AWS WAF (AdminProtectionRuleSet)
   * bloquea con 403 cualquier URL con `/admin/` en el path; la proteccion
   * de operador la hace `AguacateOperatorGuard` del backend, no el path.
   */
  createWalletForUser(usuarioId: number): Promise<BinanceWallet> {
    return firstValueFrom(
      this.http.post<BinanceWallet>(`${this.baseUrl}/for-user`, {
        usuarioId,
      }),
    );
  }

  /**
   * POST /binance-link/wallets/for-user/batch
   *
   * Crea sub-cuentas para varios usuarios de una. El backend valida la
   * lista (máx 200, sin vacíos ni duplicados) y arranca un job asíncrono;
   * responde con `{ jobId, total }`. El caller polea `getBatchStatus`.
   *
   * Errores tipados que el caller debe manejar:
   *  - 409 `BATCH_IN_PROGRESS` → ya hay un lote en curso.
   *  - 400 → lista inválida (>200, vacía o con duplicados). Validar en el
   *    front antes de enviar.
   */
  createWalletForUserBatch(usuarioIds: number[]): Promise<BatchCreateResponse> {
    return firstValueFrom(
      this.http.post<BatchCreateResponse>(`${this.baseUrl}/for-user/batch`, {
        usuarioIds,
      }),
    );
  }

  /**
   * GET /binance-link/wallets/for-user/batch/:jobId/status
   *
   * Estado del job de creación en lote. Se polea cada ~2-3s mientras
   * `status === 'processing'`. Al `completed`, `resultados[]` trae el
   * detalle por usuario (ok + walletStatus, o el error).
   *
   * 404 `BATCH_NOT_FOUND` → el job expiró o cayó en otra instancia
   * (limitación multi-instancia). El caller refresca la tabla para ver el
   * resultado real — la creación es idempotente, no se duplica.
   */
  getBatchStatus(jobId: string): Promise<BatchStatusResponse> {
    return firstValueFrom(
      this.http.get<BatchStatusResponse>(
        `${this.baseUrl}/for-user/batch/${jobId}/status`,
      ),
    );
  }

  disableWallet(id: string, reason?: string): Promise<BinanceWallet> {
    const body = reason ? { reason } : {};
    return firstValueFrom(
      this.http.post<BinanceWallet>(`${this.baseUrl}/${id}/disable`, body),
    );
  }

  /**
   * POST /binance-link/wallets/binance-sub/:subAccountId/delete
   *
   * Solicita al backend la baja de una sub-cuenta Binance. El operador
   * ingresa manualmente el `subAccountId` que devolvió Binance — no se
   * deriva del listado para forzar verificación visual antes de borrar.
   *
   * Sólo expuesto a operadores cuyo hash de email esté en el parámetro
   * `CORREOS_AUT` (mismo patrón que `users-balance.component`).
   */
  deleteBinanceSubAccount(subAccountId: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/binance-sub/${subAccountId}/delete`,
        {},
      ),
    );
  }

  /**
   * Devuelve la deposit address de la wallet identificada por `id` (path
   * operador). Default: USDT en TRC20.
   *
   * Manejo de errores que el caller debe contemplar:
   *  - 404: la wallet no existe.
   *  - 409: la wallet existe pero NO esta lista (`kycStatus` pendiente o
   *    `walletStatus` no operativo). El body trae `kycStatus`/`walletStatus`
   *    para que el front muestre el mensaje exacto.
   */
  getDepositAddressForWallet(
    id: string,
    coin: DepositCoin = 'USDT',
    network: DepositNetwork = 'TRX',
  ): Promise<DepositAddressResponse> {
    let params = new HttpParams();
    params = params.set('coin', coin);
    params = params.set('network', network);
    return firstValueFrom(
      this.http.get<DepositAddressResponse>(
        `${this.baseUrl}/${id}/deposit-address`,
        { params },
      ),
    );
  }

  /**
   * Datos consolidados del dashboard administrativo: snapshot master +
   * agregados locales de wallets. Una sola llamada para el header del
   * dashboard.
   *
   * Nunca falla por "nunca sincronizo": el backend devuelve campos master
   * en `null` y `lastSyncStatus = 'OK'` por default. La UI muestra "—".
   */
  getDashboard(): Promise<DashboardResponse> {
    return firstValueFrom(
      this.http.get<DashboardResponse>(`${this.baseUrl}/dashboard`),
    );
  }

  /**
   * Triggera un sync inmediato contra Binance fuera del cron de 5 min.
   * Idempotente: si ya hay un sync corriendo (cron o otro operador),
   * reusa el resultado.
   *
   * Devuelve el dashboard YA refrescado para que el caller actualice todo
   * en un solo round-trip.
   *
   * Latencia esperada: 2-10s segun cantidad de subs (live calls a Binance).
   * El front deberia mostrar spinner durante la promesa.
   */
  syncNow(): Promise<DashboardResponse> {
    return firstValueFrom(
      this.http.post<DashboardResponse>(
        `${this.baseUrl}/dashboard/sync-now`,
        {},
      ),
    );
  }

  /**
   * Timeline de snapshots del master capturados al cierre de cada sync.
   *
   * Lee del ring buffer en memoria del backend (cap ~24h). Si el server
   * fue reiniciado recientemente, devuelve menos snapshots o ninguno.
   * Usar para renderizar el sparkline de evolucion del saldo USDT.
   *
   * @param limit cantidad maxima a traer (default = capacidad del buffer)
   */
  getDashboardHistory(limit?: number): Promise<MasterSnapshotHistoryResponse> {
    let params = new HttpParams();
    if (limit != null) params = params.set('limit', String(limit));
    return firstValueFrom(
      this.http.get<MasterSnapshotHistoryResponse>(
        `${this.baseUrl}/dashboard/history`,
        { params },
      ),
    );
  }

  /**
   * Historial de depositos de una sub-cuenta especifica (live call a
   * Binance). Devuelve hasta 100 depositos ordenables por
   * `insertTime`.
   *
   * @throws 404 si la wallet no existe.
   * @throws 409 si la wallet no esta operativa (CREATING/ERROR/BLOCKED).
   */
  getDepositHistory(walletId: string): Promise<DepositRecord[]> {
    return firstValueFrom(
      this.http.get<DepositRecord[]>(`${this.baseUrl}/${walletId}/deposits`),
    );
  }

  /**
   * Re-valida la identidad KYC del usuario contra el backend KYC.
   * Re-corre OCR sobre la foto del documento, compara campo a campo con
   * lo registrado en `user`, y verifica facial selfie ↔ documento.
   *
   * Latencia esperada: 5-15s (depende de Textract + Rekognition).
   * Cacheado en backend por 10 min — call repetido en ese tiempo
   * devuelve `fromCache: true` sin pegarle de nuevo a AWS.
   *
   * @throws 404 si el usuarioId no existe.
   * @throws 400 si al user le faltan datos (cognitoId, fotoIdfrontal,
   *   selfie) o no es persona natural chilena.
   * @throws 502 si el KYC backend devuelve algo invalido.
   */
  revalidateKyc(usuarioId: number): Promise<RevalidateKycResponse> {
    return firstValueFrom(
      this.http.post<RevalidateKycResponse>(
        `${this.baseUrl}/users/${usuarioId}/revalidate-kyc`,
        {},
      ),
    );
  }

  /**
   * Devuelve la última revalidación KYC del usuario sin importar el TTL.
   * Pensado para precargar el modal de creación de wallet sin pegarle
   * de nuevo al KYC backend (sin OCR fresco).
   *
   * Devuelve `null` si nunca se revalidó al usuario.
   */
  getLatestKycValidation(
    usuarioId: number,
  ): Promise<RevalidateKycResponse | null> {
    return firstValueFrom(
      this.http.get<RevalidateKycResponse | null>(
        `${this.baseUrl}/users/${usuarioId}/kyc-validation/latest`,
      ),
    );
  }

  /**
   * Dispara la sincronización masiva de prevalidación KYC interna.
   * Encola un mensaje SQS por cada usuario elegible del listado.
   *
   * @throws 409 si ya hay un job RUNNING fresco (anti-duplicado server-side).
   */
  startPrevalidacionSync(): Promise<StartPrevalidacionResponse> {
    return firstValueFrom(
      this.http.post<StartPrevalidacionResponse>(
        `${this.baseUrl}/sync-prevalidacion`,
        {},
      ),
    );
  }

  /**
   * Lee el estado del último job de prevalidación + counters globales.
   * El componente lo polea cada 5s mientras `isRunning = true`.
   */
  getPrevalidacionStatus(): Promise<PrevalidacionStatusResponse> {
    return firstValueFrom(
      this.http.get<PrevalidacionStatusResponse>(
        `${this.baseUrl}/sync-prevalidacion/status`,
      ),
    );
  }
}
